<!--
  * Path: packages/frontend/src/core/components/floatingPanel/FloatingPanelComments.vue
  * Task: Floating panel managing discussions and entity comments using CBAC capabilities.
  * Update: Replaced legacy uiPermissions and role hierarchies with usePermissions (comments:create, comments:edit_own, comments:delete_any). Translated all UI strings and comments to English.
-->
<template>
  <FloatingPanelMain
    v-model="modelValue"
    title="Comments"
    :item-count="commentsCount"
  >
    <!-- Toolbar Slot -->
    <template #menu>
      <!-- Add Comment Button (+) -->
      <v-btn
        icon
        variant="text"
        density="comfortable"
        style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
        :disabled="isAddingComment || !canAddComment"
        @click="startAddComment"
      >
        <v-icon>mdi-plus</v-icon>
        <v-tooltip activator="parent" location="top">
          {{ canAddComment ? 'Add comment' : 'Permission denied: Cannot create comments' }}
        </v-tooltip>
      </v-btn>
      <v-spacer></v-spacer>

      <!-- Toggle Sort Order (Newest / Oldest first) -->
      <v-btn
        icon
        variant="text"
        density="comfortable"
        style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
        class="me-1"
        @click="toggleSortOrder"
      >
        <v-icon>{{ sortOrder === 'desc' ? 'mdi-sort-clock-descending-outline' : 'mdi-sort-clock-ascending-outline' }}</v-icon>
        <v-tooltip activator="parent" location="top">
          {{ sortOrder === 'desc' ? 'Sort: Newest first' : 'Sort: Oldest first' }}
        </v-tooltip>
      </v-btn>

      <!-- Refresh Comments Button -->
      <v-btn
        icon
        variant="text"
        density="comfortable"
        style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
        @click="refreshComments"
      >
        <v-icon>mdi-refresh</v-icon>
        <v-tooltip activator="parent" location="top">Refresh comments</v-tooltip>
      </v-btn>
    </template>

    <!-- Standard List Area for Comments -->
    <template v-slot:default>
      <div v-if="commentsCount > 0 || isAddingComment">
        <!-- Inline Input for New Comment (Draft Mode) -->
        <FloatingPanelListItem
          v-if="isAddingComment"
          :clickable="false"
          :avatarText="currentUserInitials"
          avatarColor="primary"
          :avatarSize="28"
          :title="currentUserEmail"
          subtitle="Draft"
          class="w-100"
          style="width: 100%;"
        >
          <div class="position-relative w-100 mt-2" style="width: 100%;">
            <v-textarea
              ref="newCommentInputRef"
              v-model="newCommentText"
              placeholder="Write a comment..."
              density="compact"
              variant="outlined"
              rows="3"
              auto-grow
              hide-details
              class="custom-textarea w-100"
              style="width: 100%;"
              @keydown.enter.ctrl.prevent="submitNewComment"
            ></v-textarea>
            <!-- Overlaid Action Buttons on Bottom-Right -->
            <div class="d-flex align-center position-absolute" style="bottom: 8px; right: 8px; gap: 4px; z-index: 5;">
              <v-btn
                icon
                variant="text"
                density="comfortable"
                size="small"
                style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
                :disabled="!newCommentText.length"
                @click="resetNewCommentText"
              >
                <v-icon size="18">mdi-undo</v-icon>
                <v-tooltip activator="parent" location="top">Reset</v-tooltip>
              </v-btn>
              <v-btn
                icon
                variant="text"
                density="comfortable"
                size="small"
                style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
                @click="cancelAddComment"
              >
                <v-icon size="18">mdi-close</v-icon>
                <v-tooltip activator="parent" location="top">Cancel</v-tooltip>
              </v-btn>
              <v-btn
                icon
                variant="flat"
                density="comfortable"
                color="primary"
                size="small"
                :disabled="!newCommentText.trim()"
                :loading="isSaving"
                @click="submitNewComment"
              >
                <v-icon size="18" color="white">mdi-check</v-icon>
                <v-tooltip activator="parent" location="top">Post comment</v-tooltip>
              </v-btn>
            </div>
          </div>
        </FloatingPanelListItem>

        <!-- Existing Comments List -->
        <FloatingPanelListItem
          v-for="comment in sortedComments"
          :key="comment.id"
          :clickable="canEditOrDelete(comment) && selectedCommentId !== comment.id"
          :avatarText="getInitials(comment.author)"
          avatarColor="primary"
          :avatarSize="28"
          :title="comment.author"
          :subtitle="formatDate(comment.datetime)"
          :class="{ 'selected-comment-item': selectedCommentId === comment.id }"
          class="w-100"
          style="width: 100%;"
          @click="selectComment(comment)"
        >
          <div class="d-flex flex-column w-100" style="width: 100%;">
            <!-- Edit Mode -->
            <template v-if="selectedCommentId === comment.id">
              <div class="position-relative w-100 mt-2" style="width: 100%;" @click.stop>
                <v-textarea
                  ref="editCommentInputRef"
                  v-model="editingText"
                  placeholder="Edit comment..."
                  density="compact"
                  variant="outlined"
                  rows="3"
                  auto-grow
                  hide-details
                  class="custom-textarea w-100"
                  style="width: 100%;"
                  @keydown.enter.ctrl.prevent="saveCommentEdit"
                ></v-textarea>
                <!-- Edit Action Buttons -->
                <div class="d-flex align-center position-absolute" style="bottom: 8px; right: 8px; gap: 4px; z-index: 5;">
                  <v-btn
                    v-if="canModerate || isAuthor(comment)"
                    icon
                    variant="text"
                    density="comfortable"
                    color="error"
                    size="small"
                    @click="deleteSelectedComment"
                  >
                    <v-icon size="18">mdi-delete-outline</v-icon>
                    <v-tooltip activator="parent" location="top">Delete comment</v-tooltip>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    density="comfortable"
                    style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
                    size="small"
                    :disabled="editingText === comment.text"
                    @click="resetEditingText(comment.text)"
                  >
                    <v-icon size="18">mdi-undo</v-icon>
                    <v-tooltip activator="parent" location="top">Reset changes</v-tooltip>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    density="comfortable"
                    style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
                    size="small"
                    @click="deselectComment"
                  >
                    <v-icon size="18">mdi-close</v-icon>
                    <v-tooltip activator="parent" location="top">Cancel</v-tooltip>
                  </v-btn>
                  <v-btn
                    icon="flat"
                    variant="flat"
                    density="comfortable"
                    color="primary"
                    size="small"
                    :disabled="!editingText.trim() || editingText === comment.text"
                    :loading="isSaving"
                    @click="saveCommentEdit"
                  >
                    <v-icon size="18" color="white">mdi-check</v-icon>
                    <v-tooltip activator="parent" location="top">Save</v-tooltip>
                  </v-btn>
                </div>
              </div>
            </template>
            <!-- Read-Only Presentation -->
            <template v-else>
              <div class="text-body-2 mt-2 w-100" style="white-space: pre-wrap; line-height: 1.4; width: 100%; color: rgb(var(--v-theme-hud-text)) !important;">
                {{ comment.text }}
              </div>
            </template>
          </div>
        </FloatingPanelListItem>
      </div>

      <!-- Empty State -->
      <div v-else class="text-body-2 text-center mt-8 pa-4" style="color: rgb(var(--v-theme-hud-text-secondary)) !important;">
        <v-icon size="48" style="color: rgba(var(--v-theme-hud-text-secondary), 0.35) !important;" class="mb-2">mdi-comment-outline</v-icon>
        <p>No comments available yet. Click (+) to share your thoughts!</p>
      </div>
    </template>
  </FloatingPanelMain>
</template>

<script setup lang="ts">
import { ref, computed, inject, nextTick, watch, Ref } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useDatastoreStore } from '@/store/datastore';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import { usePermissions } from '@/core/composables/usePermissions';
import { api, CommentData } from '@/core/services/api';
import type { Entity } from '@/core/types/editor';
import FloatingPanelMain from '@/core/components/floatingPanel/FloatingPanelMain.vue';
import FloatingPanelListItem from '@/core/components/floatingPanel/FloatingPanelListItem.vue';

interface FormContext {
  entity: Ref<Entity | null>;
  entityId: Ref<string | null>;
  isLocked: Ref<boolean>;
  isSecondary: boolean;
}

const props = withDefaults(defineProps<{
  isSecondary?: boolean;
}>(), {
  isSecondary: false
});

const modelValue = defineModel<boolean>({ default: false });
const workspaceStore = useWorkspaceStore();
const datastoreStore = useDatastoreStore();
const authStore = useAuthStore();
const projectStore = useProjectStore();
const { can } = usePermissions();

const formContext = inject<FormContext | null>('formContext', null);

const isAddingComment = ref<boolean>(false);
const newCommentText = ref<string>('');
const selectedCommentId = ref<string | null>(null);
const editingText = ref<string>('');
const isSaving = ref<boolean>(false);
const newCommentInputRef = ref<any>(null);
const editCommentInputRef = ref<any>(null);
const sortOrder = ref<'asc' | 'desc'>('desc');

const displayEntity = computed<Entity | null>(() => {
  if (formContext) return formContext.entity.value;
  return props.isSecondary
    ? workspaceStore.secondarySelectedEntity
    : workspaceStore.selectedEntity;
});

const comments = computed<CommentData[]>(() => {
  return displayEntity.value?.comments || [];
});

const commentsCount = computed<number>(() => comments.value.length);

const sortedComments = computed<CommentData[]>(() => {
  const rawComments = [...comments.value];
  return rawComments.sort((a, b) => {
    const dateA = new Date(a.datetime || 0).getTime();
    const dateB = new Date(b.datetime || 0).getTime();
    return sortOrder.value === 'desc' ? dateB - dateA : dateA - dateB;
  });
});

// CBAC: Capabilities cleanly governing comment authorization
const canAddComment = computed<boolean>(() => can('comments:create'));
const canModerate = computed<boolean>(() => can('comments:delete_any'));
const canEditOwn = computed<boolean>(() => can('comments:edit_own'));

const currentUserEmail = computed<string>(() => {
  return authStore.user?.email || 'guest@atlas.app';
});

const isAuthor = (comment: CommentData): boolean => {
  if (!comment?.author) return false;
  return comment.author.toLowerCase() === currentUserEmail.value.toLowerCase();
};

const canEditOrDelete = (comment: CommentData): boolean => {
  if (!comment) return false;
  if (canModerate.value) return true;
  return isAuthor(comment) && canEditOwn.value;
};

const currentUserInitials = computed<string>(() => {
  return currentUserEmail.value.charAt(0).toUpperCase();
});

const getInitials = (author: string | undefined): string => {
  if (!author) return '?';
  return author.charAt(0).toUpperCase();
};

const formatDate = (isoString: string | undefined): string => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const refreshComments = async (): Promise<void> => {
  const projectId = projectStore.loadedProject?.value;
  if (projectId) {
    await datastoreStore.fetchProjectDatastore(projectId);
  }
};

const toggleSortOrder = (): void => {
  sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
};

const focusInput = (refVar: Ref<any>): void => {
  nextTick(() => {
    if (refVar.value) {
      if (typeof refVar.value.focus === 'function') {
        refVar.value.focus();
      } else if (refVar.value.$el) {
        const textarea = refVar.value.$el.querySelector('textarea') as HTMLTextAreaElement | null;
        if (textarea) textarea.focus();
      }
    }
  });
};

const startAddComment = (): void => {
  if (!canAddComment.value) return;
  deselectComment();
  isAddingComment.value = true;
  focusInput(newCommentInputRef);
};

const resetNewCommentText = (): void => {
  newCommentText.value = '';
};

const cancelAddComment = (): void => {
  isAddingComment.value = false;
  newCommentText.value = '';
};

const selectComment = (comment: CommentData): void => {
  if (!canEditOrDelete(comment)) return;
  cancelAddComment();
  selectedCommentId.value = comment.id || null;
  editingText.value = comment.text;
  focusInput(editCommentInputRef);
};

const resetEditingText = (originalText: string): void => {
  editingText.value = originalText;
};

const deselectComment = (): void => {
  selectedCommentId.value = null;
  editingText.value = '';
};

const submitNewComment = async (): Promise<void> => {
  if (!canAddComment.value) return;
  const text = newCommentText.value.trim();
  if (!text) return;

  const projectId = projectStore.loadedProject?.value;
  const entity = displayEntity.value;
  if (!projectId || !entity || !entity.entityType) return;

  isSaving.value = true;
  try {
    const commentPayload = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15),
      text
    };

    const res = await api.comments.add(projectId, entity.entityType, entity.id, commentPayload);
    if (res.comment) {
      if (!entity.comments) entity.comments = [];
      entity.comments.push(res.comment);

      const list = datastoreStore.data?.[entity.entityType] as Entity[] | undefined;
      if (list) {
        const target = list.find((e: Entity) => e.id === entity.id);
        if (target) {
          if (!target.comments) target.comments = [];
          target.comments.push(res.comment);
        }
      }

      cancelAddComment();
      authStore.showNotification('Comment posted successfully.', 'success');
    }
  } catch (error: any) {
    authStore.showNotification(`Error: ${error.message}`, 'error');
  } finally {
    isSaving.value = false;
  }
};

const saveCommentEdit = async (): Promise<void> => {
  if (!selectedCommentId.value) return;
  const targetComment = comments.value.find((c: CommentData) => c.id === selectedCommentId.value);
  if (!targetComment || !canEditOrDelete(targetComment)) return;

  const text = editingText.value.trim();
  if (!text) return;

  const projectId = projectStore.loadedProject?.value;
  const entity = displayEntity.value;
  if (!projectId || !entity || !entity.entityType) return;

  isSaving.value = true;
  try {
    const res = await api.comments.update(projectId, entity.entityType, entity.id, selectedCommentId.value, text);
    if (res.comment) {
      if (entity.comments) {
        const commentIndex = entity.comments.findIndex((c: CommentData) => c.id === selectedCommentId.value);
        if (commentIndex !== -1) {
          entity.comments[commentIndex] = res.comment;
        }
      }

      const list = datastoreStore.data?.[entity.entityType] as Entity[] | undefined;
      if (list) {
        const target = list.find((e: Entity) => e.id === entity.id);
        if (target && target.comments) {
          const idx = target.comments.findIndex((c: CommentData) => c.id === selectedCommentId.value);
          if (idx !== -1) target.comments[idx] = res.comment;
        }
      }

      deselectComment();
      authStore.showNotification('Comment updated successfully.', 'success');
    }
  } catch (error: any) {
    authStore.showNotification(`Error: ${error.message}`, 'error');
  } finally {
    isSaving.value = false;
  }
};

const deleteSelectedComment = async (): Promise<void> => {
  if (!selectedCommentId.value) return;
  const targetComment = comments.value.find((c: CommentData) => c.id === selectedCommentId.value);
  if (!targetComment || !canEditOrDelete(targetComment)) return;

  const projectId = projectStore.loadedProject?.value;
  const entity = displayEntity.value;
  if (!projectId || !entity || !entity.entityType) return;

  if (!confirm('Do you really want to delete this comment?')) return;

  isSaving.value = true;
  try {
    await api.comments.delete(projectId, entity.entityType, entity.id, selectedCommentId.value);
    if (entity.comments) {
      entity.comments = entity.comments.filter((c: CommentData) => c.id !== selectedCommentId.value);
    }

    const list = datastoreStore.data?.[entity.entityType] as Entity[] | undefined;
    if (list) {
      const target = list.find((e: Entity) => e.id === entity.id);
      if (target && target.comments) {
        target.comments = target.comments.filter((c: CommentData) => c.id !== selectedCommentId.value);
      }
    }

    deselectComment();
    authStore.showNotification('Comment deleted successfully.', 'success');
  } catch (error: any) {
    authStore.showNotification(`Error: ${error.message}`, 'error');
  } finally {
    isSaving.value = false;
  }
};

watch(() => displayEntity.value?.id, () => {
  cancelAddComment();
  deselectComment();
});
</script>

<style scoped>
.selected-comment-item {
  background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
  border-left: none !important;
}

.custom-textarea :deep(.v-field__input) {
  padding-bottom: 44px !important;
}

.custom-textarea,
.custom-textarea :deep(.v-input__control),
.custom-textarea :deep(.v-field),
.custom-textarea :deep(.v-field__field),
.custom-textarea :deep(.v-field__input),
.custom-textarea :deep(textarea) {
  width: 100% !important;
  min-width: 100% !important;
}

.custom-textarea :deep(.v-field) {
  background-color: rgba(var(--v-theme-hud-border), 0.05) !important;
  color: rgb(var(--v-theme-hud-text)) !important;
}

.custom-textarea :deep(textarea) {
  color: rgb(var(--v-theme-hud-text)) !important;
}

.custom-textarea :deep(.v-field__outline) {
  --v-field-border-width: 1px !important;
  --v-field-border-opacity: 0.15 !important;
  color: rgb(var(--v-theme-hud-border)) !important;
}

:deep(.floating-panel-list-item) {
  border-left: none !important;
  border-right: none !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  width: 100% !important;
}

:deep(.panel-content) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
